import { useEffect, useState } from "react";
import { useNotify, useRecordContext } from "ra-core";
import { CheckCircle2, Megaphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Prospect } from "../types";

interface AcAutomation {
  id: string;
  name: string;
}

interface AcSyncRow {
  synced_at: string;
  automation_name: string | null;
}

/**
 * Push this prospect's owner into ActiveCampaign (contact + tags + custom
 * fields + deal note, optional automation enrollment) via the ac-sync edge
 * function. Mirrors the same action in the Bruno Hub deal page — both record
 * into public.ac_syncs, so a prospect synced in one app shows synced in both.
 */
export const ProspectSyncAcButton = () => {
  const record = useRecordContext<Prospect>();
  const notify = useNotify();

  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [automations, setAutomations] = useState<AcAutomation[] | null>(null);
  const [automationId, setAutomationId] = useState("");
  const [sync, setSync] = useState<AcSyncRow | null>(null);

  const prospectId = record ? Number(record.id) : null;

  useEffect(() => {
    if (!prospectId) return;
    void getSupabaseClient()
      .from("ac_syncs")
      .select("synced_at,automation_name")
      .eq("prospect_id", prospectId)
      .maybeSingle()
      .then(({ data }) => setSync(data ?? null));
  }, [prospectId]);

  if (!record || !prospectId) return null;

  const hasEmail = (record.emails ?? []).some((e) => e?.email);

  const openDialog = async () => {
    setOpen(true);
    if (automations !== null) return;
    try {
      const { data, error } = await getSupabaseClient().functions.invoke(
        "ac-sync",
        { body: { action: "automations" } },
      );
      if (error || !data?.success) {
        throw new Error(data?.error ?? error?.message ?? "automations failed");
      }
      setAutomations(data.automations ?? []);
    } catch (error: unknown) {
      setAutomations([]);
      notify(
        `Could not load automations: ${error instanceof Error ? error.message : String(error)}`,
        { type: "warning" },
      );
    }
  };

  const run = async () => {
    setBusy(true);
    try {
      const chosen = automations?.find((a) => a.id === automationId);
      const { data, error } = await getSupabaseClient().functions.invoke(
        "ac-sync",
        {
          body: {
            action: "sync",
            prospect_id: prospectId,
            automation_id: chosen?.id,
            automation_name: chosen?.name,
          },
        },
      );
      if (error) throw new Error(error.message);
      if (!data?.success) throw new Error(data?.error ?? "Sync failed");
      setSync({
        synced_at: new Date().toISOString(),
        automation_name: chosen?.name ?? null,
      });
      setOpen(false);
      notify("Synced to ActiveCampaign", { type: "success" });
    } catch (error: unknown) {
      notify(
        `Sync failed: ${error instanceof Error ? error.message : String(error)}`,
        { type: "warning" },
      );
    } finally {
      setBusy(false);
    }
  };

  const syncedOn = sync ? new Date(sync.synced_at).toLocaleDateString() : null;

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => void openDialog()}
        title={
          syncedOn
            ? `In ActiveCampaign since ${syncedOn}${sync?.automation_name ? ` (${sync.automation_name})` : ""} — click to re-sync`
            : "Push this owner to ActiveCampaign (contact + tags + optional automation)"
        }
      >
        {syncedOn ? (
          <CheckCircle2 className="size-4 text-emerald-600" />
        ) : (
          <Megaphone className="size-4" />
        )}
        {syncedOn ? "In AC" : "Sync to AC"}
      </Button>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Sync to ActiveCampaign</DialogTitle>
            <DialogDescription>
              {syncedOn
                ? `Already synced on ${syncedOn}. Re-syncing updates the contact and can enroll them in an automation.`
                : `Adds ${record.owner_name ?? "the owner"} to ActiveCampaign with tags and property fields, plus an optional automation.`}
            </DialogDescription>
          </DialogHeader>
          {hasEmail ? (
            <div className="flex flex-col gap-1">
              <Label>Automation (optional)</Label>
              <select
                className="h-9 rounded-md border bg-transparent px-2 text-sm"
                value={automationId}
                onChange={(e) => setAutomationId(e.target.value)}
              >
                <option value="">No automation — contact + tags only</option>
                {(automations ?? []).map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
              {automations === null && (
                <span className="text-xs text-muted-foreground">
                  Loading automations…
                </span>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              This prospect has no email on file — run a skip trace first.
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void run()}
              disabled={busy || !hasEmail}
            >
              {busy ? "Syncing…" : syncedOn ? "Re-sync" : "Sync"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
